import { GeoJson } from 'types/GeoJson'
import { getMessage } from '@tcc/i18n';
import { Container, Orientation } from '@tcc/fds';
import { Nullable } from '@tcc/common';
import { Activity, isArdFieldRegionReport, ReportMeasurement, YieldRegionReport } from '../../../../types';
import { getGeoJson } from '../../../../utils/geo';
import { RegionThumbnailNoteBox } from '../RegionReportThumbnailNoteBox/RegionThumbnailNoteBox';
import { RegionReportSummary } from '../RegionReportSummary/RegionReportSummary';
import { getAverageYieldText } from '../../../../utils/regionUtils';
import { SILAGE_CORN_CROP_CODE} from '../../../../constants/cropCodes';
import {
  getCropNominalWeight,
} from '../../../../utils/processUnits';
import { useAppSelector } from '../../../../utils';
import { getLocalizedAreaLabel } from '../frrStatsFormat';
import { LegacyRegionReportList } from './LegacyRegionReportList';
import { ArdRegionReportList } from './ArdRegionReportList';
import { SetItemCallback } from '../container/useRegionReportController';
import { RootLevelDetails } from '../container/types/modals';

export type RegionReportListProps = {
  activity: Activity;
  cropCode: string;
  data: RootLevelDetails;
  fieldBoundary: GeoJson;
  fieldName: string;
  isAirSeeder: boolean;
  locale: string;
  onClickItem?: SetItemCallback;
  onRegionButtonClick?: () => void;
  index: number;
  detailView?: boolean;
};

export const RegionReportList = ({
  activity,
  cropCode,
  data,
  fieldBoundary,
  fieldName,
  index,
  isAirSeeder,
  locale,
  onClickItem,
  onRegionButtonClick = () => undefined,
  detailView = false,
}: RegionReportListProps) => {
  const cropsState = useAppSelector(state => state.crops);

  let averageYield: Nullable<ReportMeasurement> = null;
  let area: Nullable<ReportMeasurement> = null;
  let averageMoisture: Nullable<ReportMeasurement> | undefined = null;
  let totalWeight: Nullable<ReportMeasurement> = null;
  let cropNominalWeight: number | Nullable<ReportMeasurement> | undefined;

  if('stats' in data) {
    averageYield = data.stats.stdYield;
    area = data.stats.harvestedArea;
    averageMoisture = data.stats.moisturePercent;
    totalWeight = data.stats.dryMass;
    cropNominalWeight = data.stats.nominalWeight;
  } else {
    const legacyData = data as YieldRegionReport;
    averageYield = legacyData.averageYield;
    area = legacyData.area;
    averageMoisture = legacyData.averageMoisture;
    totalWeight = legacyData.totalWeight;

    cropNominalWeight = getCropNominalWeight(cropCode, cropsState);
  }

  const averageYieldText = getMessage(getAverageYieldText(cropCode));
  const listView = !detailView;

  const areaUnit = getLocalizedAreaLabel(area?.u || 'ac');

  const geoJson = activity?.id ?
    getGeoJson([fieldBoundary, activity.location])
    : getGeoJson([fieldBoundary]);

  const isSilageCrop = cropCode === SILAGE_CORN_CROP_CODE

  return (
    <Container
      transparent
      orientation={Orientation.Vertical}
      width='100%'
      testId={`frr-region-report-${index}-view-${detailView ? 'detail' : 'list'}`}
    >
      {activity &&
        <RegionThumbnailNoteBox
          activity={activity}
          fieldName={fieldName}
          geoJson={geoJson}
          locale={locale}
          onRegionButtonClick={onRegionButtonClick}
        />
      }
      <RegionReportSummary
        averageYield={averageYield}
        totalWeight={totalWeight}
        averageYieldText={averageYieldText}
        area={area}
        averageMoisture={averageMoisture}
        withDryMatterYield={isSilageCrop}
        cropCode={cropCode}
        cropNominalWeight={cropNominalWeight}
      />

      { isArdFieldRegionReport(data) ? (
        <ArdRegionReportList
          data={data}
          areaUnit={areaUnit}
          cropCode={cropCode}
          index={index}
          listView={listView}
          onClickItem={onClickItem}
        />
      ) : (
        <LegacyRegionReportList
          data={data as YieldRegionReport}
          areaUnit={areaUnit}
          cropCode={cropCode}
          index={index}
          locale={locale}
          isAirSeeder={isAirSeeder}
          listView={listView}
          cropNominalWeight={typeof cropNominalWeight === 'number' ? cropNominalWeight : undefined}
          onClickItem={onClickItem}
        />
      )}
    </Container>
  );
};
